import { useState, useEffect, ReactNode } from "react";
import { Box, Paper, Typography, Skeleton, IconButton, Grid, useTheme, SxProps } from "@mui/material";
import { MouseEventHandler } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

export interface TitledCardMode {
  icon: React.ReactNode;
  content: React.ReactNode | React.ReactNode[] | null;
  title?: string;
}
interface TitledCardProps {
  title?: ReactNode;
  postfix?: JSX.Element | boolean | null;
  children?: ReactNode;
  onHeaderClick?: MouseEventHandler<HTMLDivElement>;
  headerSx?: SxProps;
  outlined?: boolean;
  color?: string;
  contrastColor?: string;
  lightSkeleton?: boolean;
  open?: boolean;
  closeContent?: JSX.Element | null | undefined | boolean | Array<JSX.Element | null | undefined | boolean>;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  modes?: TitledCardMode[];
  activeMode?: number;
  onModeChange?: Function;
  stretch?: boolean;
  style?: any;
  headerIcon?: React.ReactNode;
}

const TitledCard = ({
  title,
  postfix,
  children,
  onHeaderClick,
  headerSx,
  outlined = false,
  color,
  contrastColor,
  lightSkeleton = false,
  open,
  closeContent: closedContent,
  openIcon,
  closeIcon,
  modes,
  activeMode = 0,
  onModeChange,
  stretch = false,
  style,
  headerIcon,
}: TitledCardProps) => {
  const [modeIndex, setModeIndex] = useState(activeMode);
  const theme = useTheme();

  useEffect(() => {
    setModeIndex(activeMode);
  }, [activeMode]);

  let hasContent;
  switch (typeof children) {
    case "object":
      hasContent = children !== null && children !== undefined;
      break;
    case "boolean":
    default:
      hasContent = false;
  }
  hasContent = hasContent || !!modes?.[modeIndex]?.content;
  const titleStyle = { marginLeft: headerIcon ? 0.5 : 0, marginRight: 1 };
  const header = (
    <Box
      sx={{
        backgroundColor: color || theme.palette.primary.main,
        color: contrastColor || theme.palette.primary.contrastText,
        paddingLeft: headerIcon ? 1 : 2,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: onHeaderClick ? "pointer" : "inherit",
        borderRadius: hasContent ? "4px 4px 0 0" : "4px",
        ...(headerSx || {}),
      }}
      paddingY={1}
      onClick={onHeaderClick}
    >
      {headerIcon}
      {title || (modes && modeIndex > -1 && modes[modeIndex].title) ? (
        <Typography variant="subtitle2" sx={{ flex: 1, ...titleStyle }}>
          {title || (modes && modeIndex > -1 && modes[modeIndex].title)}
        </Typography>
      ) : (
        <Skeleton variant="text" width={200} sx={{ ...(lightSkeleton ? { bgcolor: "rgba(255,255,255,0.3)" } : {}), ...titleStyle }} />
      )}
      {modes && (
        <div>
          <Grid container spacing={2} sx={{ flexDirection: "row", justifyContent: "flex-end", paddingX: 1 }}>
            {modes
              .map((mode, i) => ({ mode, i }))
              .filter(({ i }) => i !== modeIndex)
              .map(({ mode: { icon }, i }) => (
                <Grid item key={`mode-${i}`}>
                  <IconButton
                    sx={{ mx: 0, p: 0 }}
                    onClick={() => {
                      setModeIndex(i);
                      onModeChange && onModeChange(i);
                    }}
                  >
                    {icon}
                  </IconButton>
                </Grid>
              ))}
          </Grid>
        </div>
      )}
      {open !== undefined && (open ? closeIcon || <ArrowDropUpIcon sx={{ marginX: 1 }} /> : openIcon || <ArrowDropDownIcon sx={{ marginX: 1 }} />)}
      {postfix}
    </Box>
  );

  if (outlined) {
    return (
      <div style={{ display: "flex", flexDirection: "column", ...(stretch ? { flex: 1 } : {}), ...style }}>
        {header}
        <Box sx={{ borderRadius: "0 0 4px 4px", borderColor: color, borderWidth: 1, borderStyle: "solid", borderTopWidth: 0 }}>
          {modes && modeIndex > -1 && modes[modeIndex].content}
          {children}
        </Box>
      </div>
    );
  } else {
    return (
      <Paper style={style} sx={[{ padding: 0, display: "flex", flexDirection: "column" }, stretch && { flex: 1 }]}>
        {header}
        {open !== false && modes && modeIndex > -1 && modes[modeIndex].content}
        {open !== false && children}
        {open === false && closedContent}
      </Paper>
    );
  }
};

export default TitledCard;
